import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useApp, API } from "../store";
import { Upload, Trash2, Copy, Check, Image as ImageIcon, Film, BarChart3, Settings as SettingsIcon, Share2, Calendar, Plus, Edit, Globe } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// ---------- Lesson Sessions Manager (admin edit prices/durations) ----------
export function LessonSessionsManager() {
  const { authHeaders } = useApp();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/admin/lesson-sessions`, authHeaders()).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (s) => {
    await axios.put(`${API}/admin/lesson-sessions/${s.id}`, s, authHeaders());
    load(); setEditing(null);
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir sessão?")) return;
    await axios.delete(`${API}/admin/lesson-sessions/${id}`, authHeaders());
    load();
  };

  return (
    <div data-testid="admin-lesson-sessions">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Aulas & Diárias</div>
          <h1 className="font-serif-display text-5xl text-slate-900">Sessões e Preços</h1>
          <div className="text-xs text-slate-500 mt-2">Edite duração e preços (USD + BRL fixo) das sessões e aluguéis.</div>
        </div>
        <button onClick={() => setEditing({ id: "", label: "", duration_min: 60, price_usd: 0, price_brl: 0, kind: "lesson", pt_only: false, active: true, order: rows.length + 1 })} className="btn-primary"><Plus size={14} /> Nova sessão</button>
      </div>
      <div className="mt-8 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3">ID</th>
              <th className="text-left p-3">Label</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Duração</th>
              <th className="text-left p-3">USD</th>
              <th className="text-left p-3">BRL</th>
              <th className="text-left p-3">Ativo</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200" data-testid={`lesson-session-${r.id}`}>
                <td className="p-3 font-mono text-xs">{r.id}</td>
                <td className="p-3 font-semibold">{r.label}</td>
                <td className="p-3"><span className="spec-pill">{r.kind || "lesson"}</span> {r.pt_only && <span className="ml-1 text-emerald-600">🇧🇷</span>}</td>
                <td className="p-3">{r.duration_min}m</td>
                <td className="p-3 text-sky-600 font-semibold">${r.price_usd}</td>
                <td className="p-3 text-emerald-600 font-semibold">R${r.price_brl}</td>
                <td className="p-3">{r.active ? "✓" : "—"}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEditing(r)} className="text-sky-500"><Edit size={14} /></button>
                  <button onClick={() => remove(r.id)} className="text-red-500"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Sem sessões.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <LessonSessionEditor s={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function LessonSessionEditor({ s, onClose, onSave }) {
  const [f, setF] = useState({ ...s });
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-4" data-testid="session-editor">
      <div className="bg-white border border-slate-200 max-w-lg w-full p-7 rounded-md">
        <h3 className="font-serif-display text-2xl mb-4 text-slate-900">{s.id ? "Editar sessão" : "Nova sessão"}</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="ID (slug)" v={f.id} set={(v) => setF({ ...f, id: v })} />
          <Field label="Label" v={f.label} set={(v) => setF({ ...f, label: v })} />
          <Field label="Duração (min)" type="number" v={f.duration_min} set={(v) => setF({ ...f, duration_min: Number(v) })} />
          <Field label="Tipo (lesson | rental)" v={f.kind} set={(v) => setF({ ...f, kind: v })} />
          <Field label="Preço USD" type="number" v={f.price_usd} set={(v) => setF({ ...f, price_usd: Number(v) })} />
          <Field label="Preço BRL" type="number" v={f.price_brl} set={(v) => setF({ ...f, price_brl: Number(v) })} />
          <Field label="Ordem" type="number" v={f.order} set={(v) => setF({ ...f, order: Number(v) })} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.pt_only} onChange={(e) => setF({ ...f, pt_only: e.target.checked })} /> Só Brasil</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativo</label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={() => onSave(f)} className="btn-primary" data-testid="session-save">Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Add-ons Manager (Bags etc.) ----------
export function AddonsManager() {
  const { authHeaders } = useApp();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/admin/addons`, authHeaders()).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (a) => {
    await axios.put(`${API}/admin/addons/${a.id}`, a, authHeaders());
    load(); setEditing(null);
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir extra?")) return;
    await axios.delete(`${API}/admin/addons/${id}`, authHeaders());
    load();
  };

  return (
    <div data-testid="admin-addons">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Extras</div>
          <h1 className="font-serif-display text-5xl text-slate-900">Acessórios e Extras</h1>
          <div className="text-xs text-slate-500 mt-2">Itens opcionais que aparecem como add-on no configurador (ex: Bolsa de prancha + bateria).</div>
        </div>
        <button onClick={() => setEditing({ id: "", label: "", label_pt: "", label_es: "", price_usd: 0, price_brl: 0, active: true })} className="btn-primary"><Plus size={14} /> Novo extra</button>
      </div>
      <div className="mt-8 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3">ID</th>
              <th className="text-left p-3">Label EN</th>
              <th className="text-left p-3">Label PT</th>
              <th className="text-left p-3">USD</th>
              <th className="text-left p-3">BRL</th>
              <th className="text-left p-3">Ativo</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200" data-testid={`addon-${r.id}`}>
                <td className="p-3 font-mono text-xs">{r.id}</td>
                <td className="p-3 font-semibold">{r.label}</td>
                <td className="p-3 text-slate-600">{r.label_pt}</td>
                <td className="p-3 text-sky-600 font-semibold">${r.price_usd}</td>
                <td className="p-3 text-emerald-600 font-semibold">R${r.price_brl}</td>
                <td className="p-3">{r.active ? "✓" : "—"}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEditing(r)} className="text-sky-500"><Edit size={14} /></button>
                  <button onClick={() => remove(r.id)} className="text-red-500"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Sem extras.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <AddonEditor a={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function AddonEditor({ a, onClose, onSave }) {
  const [f, setF] = useState({ ...a });
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-4" data-testid="addon-editor">
      <div className="bg-white border border-slate-200 max-w-lg w-full p-7 rounded-md">
        <h3 className="font-serif-display text-2xl mb-4 text-slate-900">{a.id ? "Editar extra" : "Novo extra"}</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="ID (slug)" v={f.id} set={(v) => setF({ ...f, id: v })} />
          <Field label="Label EN" v={f.label} set={(v) => setF({ ...f, label: v })} />
          <Field label="Label PT" v={f.label_pt || ""} set={(v) => setF({ ...f, label_pt: v })} />
          <Field label="Label ES" v={f.label_es || ""} set={(v) => setF({ ...f, label_es: v })} />
          <Field label="Preço USD" type="number" v={f.price_usd} set={(v) => setF({ ...f, price_usd: Number(v) })} />
          <Field label="Preço BRL" type="number" v={f.price_brl} set={(v) => setF({ ...f, price_brl: Number(v) })} />
          <label className="flex items-center gap-2 col-span-2"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativo</label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={() => onSave(f)} className="btn-primary" data-testid="addon-save">Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Lesson Reservations ----------
export function LessonReservations() {
  const { authHeaders } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/lesson-reservations`, authHeaders()).then((r) => setRows(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-lessons">
      <div className="overline">Aulas & Demos</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Reservas de Aulas</h1>
      <div className="text-xs text-slate-500 mt-2">Sessões reservadas via agente IA ou checkout direto.</div>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Sessão</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Contato</th>
              <th className="text-left p-3">Preferida</th>
              <th className="text-left p-3">Notas</th>
              <th className="text-left p-3">Valor</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Sem reservas ainda.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200" data-testid={`lesson-row-${r.id}`}>
                <td className="p-3 text-slate-500">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3"><span className="spec-pill">{r.session_label}</span><div className="text-[10px] text-slate-400 mt-1">{r.duration_min}m</div></td>
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3 text-slate-600 text-xs">
                  <div>{r.email}</div>
                  <div className="text-slate-400">{r.phone || "—"}</div>
                </td>
                <td className="p-3 text-slate-600 text-xs">{r.date || "—"}</td>
                <td className="p-3 text-slate-500 text-xs max-w-[260px] truncate" title={r.notes}>{r.notes || "—"}</td>
                <td className="p-3 text-sky-600 font-semibold">${r.price_usd}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Advanced Metrics ----------
export function AdvancedMetrics() {
  const { authHeaders } = useApp();
  const [stats, setStats] = useState(null);
  const [adv, setAdv] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, authHeaders()).then((r) => setStats(r.data));
    axios.get(`${API}/admin/metrics/advanced`, authHeaders()).then((r) => setAdv(r.data));
  }, []);
  if (!stats || !adv) return <div>Carregando…</div>;
  const maxViews = Math.max(1, ...(adv.top_products.map((p) => p.views)));
  const maxRevenue = Math.max(1, ...(stats.daily.map((x) => x.revenue) || [1]));

  return (
    <div data-testid="admin-metrics">
      <div className="overline">Analytics</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Métricas Avançadas</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Views totais" value={adv.total_views} icon={<BarChart3 size={18} />} />
        <Stat label="Receita" value={fmt(stats.revenue)} />
        <Stat label="Pedidos" value={stats.orders} />
        <Stat label="Clientes" value={stats.customers} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="overline mb-4">Receita diária (30d)</div>
          <div className="flex items-end gap-1 h-48">
            {stats.daily.length === 0 ? <div className="text-slate-400">Sem pedidos ainda.</div> :
              stats.daily.map((d) => {
                const h = (d.revenue / maxRevenue) * 100;
                return <div key={d.date} className="flex-1 bg-gradient-to-t from-sky-500 to-cyan-400 hover:from-sky-600 rounded-t cursor-pointer" style={{ height: `${h}%`, minHeight: "2px" }} title={`${d.date}: ${fmt(d.revenue)}`} />;
              })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="overline mb-4">Produtos mais vistos</div>
          {adv.top_products.length === 0 ? <div className="text-slate-400 text-sm">Sem visualizações ainda.</div> :
            <div className="space-y-2">
              {adv.top_products.slice(0, 8).map((p) => (
                <div key={p.slug} className="flex items-center gap-3" data-testid={`metric-product-${p.slug}`}>
                  <div className="text-xs text-slate-500 w-32 truncate">{p.slug}</div>
                  <div className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-sky-400 to-cyan-400" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                  </div>
                  <div className="text-xs font-semibold w-10 text-right">{p.views}</div>
                </div>
              ))}
            </div>}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="overline mb-4">Visualizações por país</div>
          {adv.views_by_country.length === 0 ? <div className="text-slate-400 text-sm">Sem dados ainda.</div> :
            <div className="grid grid-cols-2 gap-2">
              {adv.views_by_country.slice(0, 12).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border border-slate-100 rounded p-2" data-testid={`metric-country-${c.country}`}>
                  <span className="font-mono text-slate-700">{c.country || "??"}</span>
                  <span className="text-sky-600 font-semibold">{c.views}</span>
                </div>
              ))}
            </div>}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="overline mb-4">Localização de clientes</div>
          {adv.user_locations.length === 0 ? <div className="text-slate-400 text-sm">Sem dados ainda.</div> :
            <div className="grid grid-cols-2 gap-2">
              {adv.user_locations.map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border border-slate-100 rounded p-2">
                  <span className="font-mono text-slate-700">{c.country || "??"}</span>
                  <span className="text-emerald-600 font-semibold">{c.count}</span>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-md">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {icon && <div className="text-sky-500">{icon}</div>}
      </div>
      <div className="font-serif-display text-4xl mt-2 text-sky-600">{value}</div>
    </div>
  );
}

// ---------- Media Library (filesystem upload) ----------
export function MediaLibrary({ onPickUrl }) {
  const { authHeaders, token } = useApp();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingWeb, setUploadingWeb] = useState(false);
  const [copied, setCopied] = useState("");
  const fileRef = useRef(null);
  const webFileRef = useRef(null);

  const load = () => axios.get(`${API}/admin/uploads`, authHeaders()).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, data, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      if (onPickUrl) onPickUrl(absoluteUrl(res.data.url));
      load();
    } catch (err) {
      alert("Upload falhou: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadWeb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWeb(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload-web`, data, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      if (onPickUrl) onPickUrl(absoluteUrl(res.data.url));
      const origSize = res.data.original_size ? (res.data.original_size / 1024).toFixed(0) : "?";
      const newSize = res.data.size ? (res.data.size / 1024).toFixed(0) : "?";
      alert(`Imagem otimizada!\nOriginal: ${origSize} KB → Web: ${newSize} KB\nFormato: WebP`);
      load();
    } catch (err) {
      alert("Upload web falhou: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingWeb(false);
      if (webFileRef.current) webFileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir mídia?")) return;
    await axios.delete(`${API}/admin/uploads/${id}`, authHeaders());
    load();
  };

  const copyUrl = async (url) => {
    const abs = absoluteUrl(url);
    await navigator.clipboard.writeText(abs);
    setCopied(url);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Mídia</div>
          <h1 className="font-serif-display text-5xl text-slate-900">Biblioteca</h1>
          <div className="text-xs text-slate-500 mt-2">Upload de imagens e vídeos. Use a URL nos editores de produto/blog/conteúdo.</div>
        </div>
        <div className="flex gap-3">
          <label className="btn-primary cursor-pointer" data-testid="upload-web-btn">
            <Globe size={14} /> {uploadingWeb ? "Otimizando…" : "Upload Image Web Format"}
            <input ref={webFileRef} type="file" accept="image/*" onChange={uploadWeb} className="hidden" />
          </label>
          <label className="btn-ghost cursor-pointer" data-testid="upload-btn">
            <Upload size={14} /> {uploading ? "Enviando…" : "Enviar arquivo"}
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={upload} className="hidden" />
          </label>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {items.length === 0 && <div className="col-span-full text-slate-400 text-sm">Sem arquivos ainda.</div>}
        {items.map((it) => (
          <div key={it.id} className="border border-slate-200 rounded-md overflow-hidden bg-white group relative" data-testid={`media-${it.id}`}>
            <div className="aspect-square bg-slate-100 grid place-items-center">
              {it.kind === "video" ? (
                <video src={absoluteUrl(it.url)} muted className="w-full h-full object-cover" />
              ) : (
                <img src={absoluteUrl(it.url)} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-2 text-[10px] text-slate-500 truncate flex items-center gap-1">
              {it.kind === "video" ? <Film size={11} /> : <ImageIcon size={11} />}
              {it.original_name || it.filename}
              {it.optimized && <span className="ml-auto bg-emerald-100 text-emerald-700 px-1 rounded text-[9px] font-bold">WebP</span>}
            </div>
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => copyUrl(it.url)} className="bg-white/95 hover:bg-sky-500 hover:text-white rounded p-1.5 shadow" title="Copiar URL">
                {copied === it.url ? <Check size={12} /> : <Copy size={12} />}
              </button>
              <button onClick={() => del(it.id)} className="bg-white/95 hover:bg-red-500 hover:text-white rounded p-1.5 shadow text-red-500"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function absoluteUrl(rel) {
  if (!rel) return "";
  if (/^https?:/.test(rel)) return rel;
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base}${rel.startsWith("/") ? "" : "/"}${rel}`;
}

// ---------- Embeds (Instagram + Site Settings + GA) ----------
export function EmbedsAndSettings() {
  const { authHeaders } = useApp();
  const [ig, setIg] = useState({ embed_html: "", post_urls: [] });
  const [postUrlsText, setPostUrlsText] = useState("");
  const [settings, setSettings] = useState({ ga_measurement_id: "", instagram_handle: "" });
  const [savedIg, setSavedIg] = useState(false);
  const [savedSet, setSavedSet] = useState(false);

  useEffect(() => {
    axios.get(`${API}/instagram/embed`).then((r) => {
      setIg(r.data);
      setPostUrlsText((r.data.post_urls || []).join("\n"));
    });
    axios.get(`${API}/site/settings`).then((r) => setSettings(r.data));
  }, []);

  const saveIg = async () => {
    const urls = postUrlsText.split("\n").map((s) => s.trim()).filter(Boolean);
    await axios.post(`${API}/admin/instagram/embed`, { embed_html: ig.embed_html, post_urls: urls }, authHeaders());
    setIg({ ...ig, post_urls: urls });
    setSavedIg(true);
    setTimeout(() => setSavedIg(false), 1500);
  };

  const saveSettings = async () => {
    await axios.post(`${API}/admin/site/settings`, settings, authHeaders());
    setSavedSet(true);
    setTimeout(() => setSavedSet(false), 1500);
  };

  return (
    <div data-testid="admin-embeds">
      <div className="overline">Integrações</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Embeds & Configurações</h1>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Site Settings */}
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-3"><SettingsIcon size={16} className="text-sky-500" /> <div className="overline">Configurações gerais</div></div>
          <h3 className="font-serif-display text-2xl text-slate-900 mb-5">Site</h3>
          <label className="block mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">Google Analytics 4 — Measurement ID</div>
            <input value={settings.ga_measurement_id || ""} onChange={(e) => setSettings({ ...settings, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX" className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none font-mono" data-testid="ga-id-input" />
            <div className="text-[10px] text-slate-400 mt-1">Quando preenchido, GA4 carrega automaticamente em todas as páginas públicas.</div>
          </label>
          <label className="block mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">Instagram handle</div>
            <input value={settings.instagram_handle || ""} onChange={(e) => setSettings({ ...settings, instagram_handle: e.target.value })} placeholder="sharkxelectric" className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none" />
          </label>
          <button onClick={saveSettings} className="btn-primary" data-testid="settings-save">{savedSet ? "✓ Salvo" : "Salvar"}</button>
        </div>

        {/* Instagram Embed */}
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-3"><Share2 size={16} className="text-pink-500" /> <div className="overline">Instagram feed</div></div>
          <h3 className="font-serif-display text-2xl text-slate-900 mb-2">@sharkxelectric</h3>
          <p className="text-xs text-slate-500 mb-5">
            <strong>Opção A (recomendada):</strong> Cole o snippet de embed do <a href="https://elfsight.com/instagram-feed-instashow/" target="_blank" rel="noreferrer" className="text-sky-500 underline">Elfsight</a> ou SnapWidget — eles puxam automaticamente os 10 últimos posts com autoplay nos vídeos.<br/>
            <strong>Opção B:</strong> Cole URLs de posts individuais (uma por linha) — vamos usar o embed oficial do Instagram para cada um.
          </p>
          <label className="block mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">A · Snippet de embed (Elfsight/SnapWidget)</div>
            <textarea value={ig.embed_html || ""} onChange={(e) => setIg({ ...ig, embed_html: e.target.value })} rows={5} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-xs font-mono focus:border-sky-500 focus:outline-none" placeholder='<div class="elfsight-app-..."></div><script ...></script>' data-testid="ig-embed-html" />
          </label>
          <label className="block mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">B · URLs dos posts (1 por linha — máx 10)</div>
            <textarea value={postUrlsText} onChange={(e) => setPostUrlsText(e.target.value)} rows={5} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-xs font-mono focus:border-sky-500 focus:outline-none" placeholder="https://www.instagram.com/p/SHORTCODE/" data-testid="ig-post-urls" />
          </label>
          <button onClick={saveIg} className="btn-primary" data-testid="ig-save">{savedIg ? "✓ Salvo" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Improved Product Editor with Media Picker ----------
export function ProductEditorPro({ product, onClose, onSave }) {
  const { authHeaders, token } = useApp();
  const [showMedia, setShowMedia] = useState(null); // null | "images" | "card_images"
  const [f, setF] = useState({
    slug: product.slug || "", name: product.name || "", category: product.category || "efoils",
    tagline: product.tagline || "", description: product.description || "",
    base_price: product.base_price || 0,
    price_brl_override: product.price_brl_override ?? "",
    carbon_upcharge: product.carbon_upcharge || 0,
    colors: (product.colors || []).join(","), sizes: (product.sizes || []).join(","),
    wing_options: (product.wing_options || []).join(","), stabilizer_options: (product.stabilizer_options || []).join(","),
    images: product.images || [],
    card_images: product.card_images || [],
    battery_options: JSON.stringify(product.battery_options || [], null, 2),
    specs: JSON.stringify(product.specs || {}, null, 2),
    featured: !!product.featured, is_accessory: !!product.is_accessory, stock: product.stock ?? 100,
    levels: product.levels || [], // beginner | intermediate | pro (multi-select)
  });

  const save = async () => {
    const payload = {
      slug: f.slug, name: f.name, category: f.category, tagline: f.tagline, description: f.description,
      base_price: Number(f.base_price),
      price_brl_override: f.price_brl_override !== "" && f.price_brl_override != null ? Number(f.price_brl_override) : null,
      carbon_upcharge: Number(f.carbon_upcharge),
      colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      wing_options: f.wing_options.split(",").map((s) => s.trim()).filter(Boolean),
      stabilizer_options: f.stabilizer_options.split(",").map((s) => s.trim()).filter(Boolean),
      images: f.images,
      card_images: f.card_images,
      battery_options: JSON.parse(f.battery_options || "[]"),
      specs: JSON.parse(f.specs || "{}"),
      featured: f.featured, is_accessory: f.is_accessory, stock: Number(f.stock),
      levels: f.levels,
    };
    if (product.id) await axios.put(`${API}/products/${product.id}`, payload, authHeaders());
    else await axios.post(`${API}/products`, payload, authHeaders());
    onSave(); onClose();
  };

  const uploadDirect = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    const res = await axios.post(`${API}/admin/upload`, data, {
      headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
    });
    setF((prev) => ({ ...prev, [key]: [...prev[key], absoluteUrl(res.data.url)] }));
    e.target.value = "";
  };

  const removeUrl = (key, url) => setF((prev) => ({ ...prev, [key]: prev[key].filter((u) => u !== url) }));

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6 overflow-y-auto" data-testid="product-editor-pro">
      <div className="bg-white border border-slate-200 max-w-4xl w-full p-8 max-h-[92vh] overflow-y-auto rounded-md">
        <h3 className="font-serif-display text-3xl mb-6 text-slate-900">{product.id ? "Editar" : "Novo"} produto</h3>
        <div className="grid grid-cols-2 gap-3 font-ui text-sm">
          <Field label="Slug" v={f.slug} set={(v) => setF({ ...f, slug: v })} />
          <Field label="Nome" v={f.name} set={(v) => setF({ ...f, name: v })} />
          <Field label="Categoria" v={f.category} set={(v) => setF({ ...f, category: v })} />
          <Field label="Tagline" v={f.tagline} set={(v) => setF({ ...f, tagline: v })} />
          <Field label="Preço base (USD)" type="number" v={f.base_price} set={(v) => setF({ ...f, base_price: v })} />
          <Field label="Preço BRL fixo (opcional)" type="number" v={f.price_brl_override ?? ""} set={(v) => setF({ ...f, price_brl_override: v })} />
          <Field label="Upgrade carbon" type="number" v={f.carbon_upcharge} set={(v) => setF({ ...f, carbon_upcharge: v })} />
          <Field label="Cores (csv)" v={f.colors} set={(v) => setF({ ...f, colors: v })} />
          <Field label="Tamanhos (csv)" v={f.sizes} set={(v) => setF({ ...f, sizes: v })} />
          <Field label="Asas (csv)" v={f.wing_options} set={(v) => setF({ ...f, wing_options: v })} />
          <Field label="Estabilizadores (csv)" v={f.stabilizer_options} set={(v) => setF({ ...f, stabilizer_options: v })} />
          <Field label="Estoque" type="number" v={f.stock} set={(v) => setF({ ...f, stock: v })} />

          <MediaList label="Imagens da página de produto" urls={f.images} onUpload={(e) => uploadDirect(e, "images")} onRemove={(u) => removeUrl("images", u)} testid="images-uploader" />
          <MediaList label="Imagens do CARD da homepage (hover)" urls={f.card_images} onUpload={(e) => uploadDirect(e, "card_images")} onRemove={(u) => removeUrl("card_images", u)} testid="card-images-uploader" />

          <Field label="Baterias (JSON)" v={f.battery_options} set={(v) => setF({ ...f, battery_options: v })} full textarea />
          <Field label="Specs (JSON)" v={f.specs} set={(v) => setF({ ...f, specs: v })} full textarea />
          <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Destaque</label>
          <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={f.is_accessory} onChange={(e) => setF({ ...f, is_accessory: e.target.checked })} /> Acessório</label>
          <div className="col-span-2 mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-ui">Nível do produto (selecione um ou mais)</div>
            <div className="flex flex-wrap gap-4">
              {[
                { key: "beginner", label: "Iniciante" },
                { key: "intermediate", label: "Intermediário" },
                { key: "pro", label: "Pro" },
              ].map((level) => (
                <label key={level.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={f.levels.includes(level.key)}
                    onChange={(e) => {
                      const newLevels = e.target.checked
                        ? [...f.levels, level.key]
                        : f.levels.filter((l) => l !== level.key);
                      setF({ ...f, levels: newLevels });
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500"
                  />
                  <span className="text-sm text-slate-700">{level.label}</span>
                </label>
              ))}
            </div>
          </div>
          <textarea placeholder="Descrição" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="col-span-2 bg-white border border-slate-300 px-3 py-2 rounded"></textarea>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={save} className="btn-primary" data-testid="editor-save">Salvar</button>
        </div>
      </div>
    </div>
  );
}

function MediaList({ label, urls, onUpload, onRemove, testid }) {
  return (
    <div className="col-span-2 border border-slate-200 rounded-md p-4 bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-ui">{label} ({urls.length})</div>
        <label className="text-xs px-3 py-1 rounded bg-sky-500 text-white cursor-pointer hover:bg-sky-600 inline-flex items-center gap-1" data-testid={testid}>
          <Upload size={12} /> Upload
          <input type="file" accept="image/*,video/*" onChange={onUpload} className="hidden" />
        </label>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
        {urls.length === 0 && <div className="col-span-full text-slate-400 text-xs">Nenhum arquivo. Faça upload acima.</div>}
        {urls.map((u, i) => {
          const v = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u);
          return (
            <div key={i} className="relative aspect-square rounded overflow-hidden border border-slate-200 group">
              {v ? <video src={u} muted className="w-full h-full object-cover" /> : <img src={u} alt="" className="w-full h-full object-cover" />}
              <button onClick={() => onRemove(u)} className="absolute top-1 right-1 bg-white/95 hover:bg-red-500 hover:text-white rounded p-1 opacity-0 group-hover:opacity-100"><Trash2 size={11} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text", full, textarea }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">{label}</div>
      {textarea ? (
        <textarea value={v} onChange={(e) => set(e.target.value)} rows={4} className="w-full bg-white border border-slate-300 px-3 py-2 rounded focus:border-sky-500 focus:outline-none font-mono text-xs" />
      ) : (
        <input type={type} value={v} onChange={(e) => set(e.target.value)} className="w-full bg-white border border-slate-300 px-3 py-2 rounded focus:border-sky-500 focus:outline-none" />
      )}
    </label>
  );
}
